import { skipToken } from "@reduxjs/toolkit/query";
import type {
  AccountGradingRecording,
  GradingId,
} from "@repo/app-types/entities/teammatePerformanceSummary";
import type { FC, Ref, RefObject } from "react";
import { usePlaybookGradingCallOutlineQuery } from "../../api/entities/performancePlaybook.js";
import { useRecordingToolActivitiesQuery } from "../../api/entities/recording.js";
import { AudioPlayer } from "../../components/audioPlayer/audioPlayer.js";
import { TranscriptScrollPane } from "../../components/transcript/transcriptScrollPane.js";
import { CallOutlineNavigationPopover } from "../teammatePerformance/callOutlineNavigationPopover.js";
import type { QuoteMatchMap } from "../teammatePerformance/gradeValidationHelpers.js";
import {
  GradingTranscriptWithIndex,
  type GradingTranscriptWithIndexHandle,
} from "../teammatePerformance/gradingTranscriptWithIndex.js";
import { TranscriptVideoPanel } from "../teammatePerformance/transcriptVideoPanel.js";
import { useTranscriptOutlineSync } from "../teammatePerformance/useTranscriptOutlineSync.js";

interface TranscriptViewerPaneProps {
  gradingId: GradingId;
  sortedRecordings: readonly AccountGradingRecording[];
  quoteMatches: QuoteMatchMap;
  transcriptRef: Ref<GradingTranscriptWithIndexHandle>;
  transcriptContainerRef: RefObject<HTMLDivElement | null>;
}

/**
 * Reusable right-pane transcript viewer with call outline navigation,
 * evidence highlighting, and an audio player.
 *
 * Used by the KB adherence statement slideover, signal match slideover,
 * and customer interaction marker slideover.
 */
export const TranscriptViewerPane: FC<TranscriptViewerPaneProps> = ({
  gradingId,
  sortedRecordings,
  quoteMatches,
  transcriptRef,
  transcriptContainerRef,
}) => {
  const hasTranscript = sortedRecordings.some((r) => r.transcript);

  // Fetch tool activities for all recordings
  const recordingIds = sortedRecordings.map((r) => r.id);
  const { data: toolActivitiesByRecordingId } = useRecordingToolActivitiesQuery(
    recordingIds.length > 0 ? recordingIds : skipToken,
  );

  const { callPosition, scrollToCallPosition } = useTranscriptOutlineSync({
    transcriptContainerRef,
    sortedRecordings,
  });

  // Fetch call outline for overlay navigation
  const {
    data: callOutline,
    isLoading: isCallOutlineLoading,
    isError: isCallOutlineError,
  } = usePlaybookGradingCallOutlineQuery({ gradingId });

  return (
    <>
      {/* Transcript pane */}
      <div className="flex min-h-0 flex-col bg-white">
        <div className="space-y-2 border-b border-border-light p-4">
          <h3 className="text-sm font-semibold text-foreground">
            Call transcript
          </h3>
          <CallOutlineNavigationPopover
            callOutline={callOutline}
            isLoading={isCallOutlineLoading}
            isError={isCallOutlineError}
            callPosition={callPosition}
            onCallPositionRequest={scrollToCallPosition}
          />
        </div>
        <TranscriptVideoPanel recordings={sortedRecordings} />
        <TranscriptScrollPane
          scrollContainerRef={transcriptContainerRef}
          className="px-4"
        >
          <div className="pb-4">
            {hasTranscript ? (
              <GradingTranscriptWithIndex
                ref={transcriptRef}
                recordings={sortedRecordings}
                quoteMatches={quoteMatches}
                scrollToCallPosition={scrollToCallPosition}
                toolActivitiesByRecordingId={toolActivitiesByRecordingId}
              />
            ) : (
              <p className="text-sm text-muted-foreground">
                No transcript available
              </p>
            )}
          </div>
        </TranscriptScrollPane>
      </div>

      {/* Audio player spans full width */}
      <div className="col-span-full">
        <AudioPlayer />
      </div>
    </>
  );
};
